import {Box, Heading, Image, Text} from "grommet";
import React from "react";
import {formatCurrency} from "../../utils/numbers";
import { HideInPrint, VisibleOnlyInPrint } from "../../utils/print";
import { snakeCase } from "../../utils/string";
import Button from "../Button";
import StatusMessage from "../StatusMessage";

/*
    What gets printed is a receipt for a payment, so it says so. It used to head every
    printed page "Invoice <identifier>", which described the document being paid rather
    than the thing in the payer's hand - and on a deposit, where there is no invoice and
    no identifier, it printed a bare "Invoice" with a blank name above it.

    `merchantName` and `subject` are the direct form. `data` is the invoice checkout's
    existing shape and still works untouched: the merchant comes off preference and the
    subject becomes "Invoice <identifier>", so an invoice receipt still names its invoice.
*/
const PrintPaymentHeader = (props) => {
    const {data, merchantName, subject, logoUrl} = props;
    const {preference, identifier} = data || {};
    const {legal_name: legalName} = preference || {};

    const name = merchantName || legalName;
    const logo = logoUrl || preference?.logo_url || preference?.logo;
    const subjectLine = subject || (identifier ? `Invoice ${identifier}` : undefined);

    return (
        <VisibleOnlyInPrint>
            <Box direction="row" justify="between" margin={{top: "large"}} align="end">
                <Box direction="column" gap="small">
                    {/* The merchant's mark, not ours: this is their receipt to their
                        customer, and we are the rail underneath it. No logo simply
                        leaves the name to head the page, as it always did. */}
                    {logo && (
                        <Box height="40px" width={{max: "180px"}} align="start">
                            <Image src={logo} alt={name || ''} fit="contain" alignSelf="start" />
                        </Box>
                    )}
                    {name && (<Text size="xlarge">{name}</Text>)}
                    <Text size="medium">Payment receipt</Text>
                    {subjectLine && (<Text size="medium">{subjectLine}</Text>)}
                </Box>
            </Box>
            <hr />
        </VisibleOnlyInPrint>
    );
};

const PrintStatementHeader = ({companyHeader, data, hideBalance}) => {
    const {amount, currency} = data || {};
    return (
        <VisibleOnlyInPrint>
            {companyHeader}
            <Box margin={{top: 'medium'}}>
                {/* {!hideBalance && amount && currency && <Text weight="bold" size="xlarge">Balance: {formatCurrency(amount, currency)}</Text>} */}
            </Box>
            <hr />
        </VisibleOnlyInPrint>
    );
}

const BasePaymentForm = (props) => {
    const {
        heading,
        children,
        primaryButton,
        onCancel,
        cancelText,
        cancelDisabled,
        headingProps,
        printable,
        printOptions,
        error,
        isBulkPay,
        footer,
    } = props;

    return (
        <>
            { printable && !isBulkPay && (<PrintPaymentHeader {...printOptions} />)}
            { printable && isBulkPay && (<PrintStatementHeader {...printOptions} />)}
            <Box alignContent="start" pad="none">
                {heading && (
                    <Heading
                        level={3}
                        size="small"
                        margin={{vertical: 'small'}}
                        data-testid="payment-form-heading"
                        {...headingProps}>
                        {heading}
                    </Heading>)
                }
                <Box>
                    { error && (<StatusMessage apiError={error} />) }
                    {children}
                </Box>
                { (primaryButton || onCancel) && (
                    <HideInPrint
                        direction="row-reverse"
                        fill="horizontal"
                        justify="between"
                        margin={{top: 'medium'}}
                        align="end">
                        {primaryButton}
                        {onCancel && (
                            <Button
                                event={{section: 'payment_form', element: 'cancel', description: snakeCase(heading)}}
                                secondary
                                /*
                                    This button sits outside `children`, so a form that
                                    hides its own fields while a payment is in flight does
                                    not hide this one. Leaving it live lets the payer
                                    unmount the payment form mid-confirmation. Opt-in, so
                                    forms that never reach that state are unaffected.
                                */
                                disabled={cancelDisabled}
                                onClick={() => {
                                    onCancel();
                                }}
                                key="cancel"
                                hoverIndicator
                                label={cancelText || 'Back'} />
                        )}
                    </HideInPrint>)
                }
                <Box margin={{top: 'small'}}>
                    {footer ? footer : (<></>)}
                </Box>
            </Box>
        </>
    );
}

export default BasePaymentForm;